import type { CartItem } from "./cart";
import type { WidgetEvent, WidgetService } from "../types";

type ServiceImages = Pick<WidgetService, "id" | "hero_image_url"> & {
  units: ReadonlyArray<{ id: string; image_url: string | null }>;
  special_date_events?: ReadonlyArray<{ id: string; hero_image_url: string | null }>;
};
type EventImages = Pick<WidgetEvent, "id" | "hero_image_url"> & {
  ticket_types?: ReadonlyArray<{ id: string; image_url?: string | null }>;
};

/** Only persisted public catalogue URLs, never upload previews or executable schemes. */
function catalogueImage(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const src = value.trim();
  if (!/^https?:\/\//i.test(src) || /[\u0000-\u001f\u007f]/.test(src)) return undefined;
  try {
    const url = new URL(src);
    return url.username || url.password ? undefined : src;
  } catch {
    return undefined;
  }
}

/**
 * Resolve each line against this widget's loaded catalogue, not the active tab
 * or a URL in persisted cart metadata. The renderer applies the usual image
 * proxy; retaining the original URL here avoids coupling this pure lookup to
 * build-time proxy settings. Missing/removed catalogue entries stay imageless.
 */
export function checkoutSummaryImages(
  items: readonly Pick<CartItem, "id" | "serviceId" | "serviceType" | "unitId" | "metadata">[],
  services: readonly ServiceImages[],
  events: readonly EventImages[],
): Record<string, string> {
  const images: Record<string, string> = Object.create(null);
  for (const item of items) {
    let image: string | undefined;
    if (item.serviceType === "event_ticket") {
      const event = events.find((entry) => entry.id === item.metadata?.event_id);
      const ticket = event?.ticket_types?.find((entry) => entry.id === item.unitId);
      image = catalogueImage(ticket?.image_url) ?? catalogueImage(event?.hero_image_url);
    } else {
      const service = services.find((entry) => entry.id === item.serviceId);
      if (!service) continue;
      const specialDate = service.special_date_events?.find(
        (entry) => entry.id === item.metadata?.special_date_event_id,
      );
      const unit = service.units.find((entry) => entry.id === item.unitId);
      image = catalogueImage(specialDate?.hero_image_url)
        ?? catalogueImage(unit?.image_url)
        ?? catalogueImage(service.hero_image_url);
    }
    if (image) images[item.id] = image;
  }
  return images;
}
